import {Directive, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {LocationWithVoorraad} from '../pot/location-with-voorraad';
import {SortingService} from '../../_services/sorting.service';
import {Stock} from '../stock';
import {AbstractBaseCountComponent} from './abstract-base-count.component';

@Directive()
export abstract class AbstractLocationCountComponent<TStock extends Stock = Stock>
  extends AbstractBaseCountComponent<TStock>
  implements OnInit {

  allBeds: LocationWithVoorraad[] = [];
  visibleBeds: LocationWithVoorraad[] = [];
  filteredAndSortedBeds: LocationWithVoorraad[] = [];
  displayedColumns = ['naam', 'rest', 'vrij', 'vr', 'potmaat'];
  higherThanPriority = 0;
  idToBeFetched: number;

  protected abstract readonly detailRoutePrefix: string;

  constructor(
    protected router: Router,
    sortingService?: SortingService
  ) {
    super(sortingService);
  }

  protected abstract fetchLocations(): Observable<LocationWithVoorraad[]>;
  protected abstract fetchStockItemById(id: number): Observable<TStock>;
  protected abstract updateStockItem(stock: TStock): Observable<any>;

  ngOnInit() {
    this.setupSearchFieldBehaviour();
    this.fetchAllBeds().then(() => {
      this.setBedsVisible();
    });
  }

  protected setupSearchFieldBehaviour() {
    this.searchTermSubject.pipe(
      takeUntil(this.onDestroy$),
      distinctUntilChanged(),
    ).subscribe(_ => this.sortBedsOnLocationWithSlider());
  }

  async fetchAllBeds() {
    this.fetchingBedsSubject.next(true);
    try {
      this.allBeds = await this.fetchLocations().toPromise();
    } catch (error) {
      console.error('Unable to get all beds: ' + error);
    }
    this.sortBedsOnLocationWithSlider();
    this.fetchingBedsSubject.next(false);
  }

  syncVisibleBedsWithAllBeds() {
    this.visibleBeds = this.filteredAndSortedBeds.slice(this.firstVisibleBedIndex, this.firstVisibleBedIndex + this.AMOUNT_BEDS_VISIBLE);
  }

  loadMoreItemsBottom() {
    const bedsToAdd: LocationWithVoorraad[] = this.filteredAndSortedBeds.slice(
      this.firstVisibleBedIndex + this.AMOUNT_BEDS_VISIBLE,
      this.firstVisibleBedIndex + this.AMOUNT_BEDS_VISIBLE + 5
    );
    bedsToAdd.forEach(bed => this.visibleBeds.push(bed));
    this.visibleBeds.splice(0, bedsToAdd.length);
    this.firstVisibleBedIndex += bedsToAdd.length;
  }

  loadMoreItemsTop() {
    const bedsToAdd: LocationWithVoorraad[] = this.filteredAndSortedBeds
      .slice(Math.max(0, this.firstVisibleBedIndex - 5), this.firstVisibleBedIndex);
    bedsToAdd.reverse().forEach(bed => this.visibleBeds.unshift(bed));
    this.firstVisibleBedIndex -= bedsToAdd.length;
    this.visibleBeds.splice(-bedsToAdd.length, bedsToAdd.length);
  }

  public async onAttach() {
    if (this.allBeds.length <= 0) {
      await this.fetchAllBeds();
      this.syncVisibleBedsWithAllBeds();
    } else {
      if (this.idToBeFetched) {
        await this.fetchStockById(this.idToBeFetched);
      }
      this.syncVisibleBedsWithAllBeds();
    }
    await super.onAttach();
    this.fetchingBedsSubject.next(false);
  }

  setBedsVisible() {
    this.visibleBeds = [];
    this.firstVisibleBedIndex = 0;
    this.syncVisibleBedsWithAllBeds();
  }

  public sortBedsOnLocationWithSlider() {
    if (this.searchTerm) {
      this.filteredAndSortedBeds = (this.allBeds || []).filter(val => this.isPresentOnLocation(val.location));
      if (this.filteredAndSortedBeds.length > 0) {
        this.noBedsFoundMessage = undefined;
        this.firstVisibleBedIndex = 0;
      } else {
        this.noBedsFoundMessage = 'Geen bedden gevonden voor zoekterm: ' + this.searchTerm;
      }
    } else {
      this.filteredAndSortedBeds = this.allBeds || [];
    }
    if (this.higherThanPriority === undefined || this.higherThanPriority === null) {
      this.higherThanPriority = 0;
    }
    const lastIndex = this.filteredAndSortedBeds.findIndex(val => this.higherThanPriority > val.priority);
    if (lastIndex !== -1) {
      this.filteredAndSortedBeds = this.filteredAndSortedBeds.slice(0, lastIndex);
    }
    this.filteredAndSortedBeds = this.filteredAndSortedBeds
      .filter(val => val && val.location !== null)
      .sort((a, b) => {
        if (this.useCustomSort && this.sortingService) {
          return this.sortingService.customLocationCompare(a.location, b.location);
        }
        return (a.location || '').localeCompare(b.location || '');
      });
    this.syncVisibleBedsWithAllBeds();
  }

  async fetchStockById(sleutel: number) {
    if (!sleutel) {
      return;
    }
    try {
      const updatedStock = await this.fetchStockItemById(sleutel).toPromise();
      if (updatedStock) {
        this.findAndReplaceUpdatedStock(updatedStock);
      }
    } catch (error) {
      console.error('Unable to get voorraad for key ' + sleutel + '\n' + error);
    }
  }

  navigateToStock(voorraad: TStock) {
    this.idToBeFetched = voorraad.id;
    this.router.navigate([`${this.detailRoutePrefix}/${voorraad.id}`]);
  }

  isPresentOnLocation(bed: string): boolean {
    if (bed && this.searchTerm) {
      return bed.toLowerCase().startsWith(this.searchTerm.toLowerCase());
    }
    return true;
  }

  formatThumbLabel(value: number): string {
    return Math.round(value / 10) + '%';
  }

  calculateAmountOfRows(): number {
    let amount = 0;
    (this.filteredAndSortedBeds || []).forEach(val => {
      amount += (val.stocks || []).length;
    });
    return amount;
  }

  async setLastInspectionForLocation(event: MouseEvent, bed: LocationWithVoorraad) {
    event.stopPropagation();
    const updatePromises = (bed.stocks || []).map((stock: TStock) => this.setLastInspection(event, stock));
    await Promise.all(updatePromises);
  }

  async setLastInspection(event: MouseEvent, stock: TStock) {
    event.stopPropagation();
    this.applyInspectionToggle(stock);

    return this.updateStockItem(stock)
      .subscribe((updatedStock: TStock) => {
        this.findAndReplaceUpdatedStock(updatedStock);
      });
  }

  findAndReplaceUpdatedStock(stock: TStock) {
    (this.filteredAndSortedBeds || []).forEach(value => {
      const correctStock = (value.stocks || [])
        .find(val => val.id === stock.id);
      if (correctStock) {
        value.stocks[value.stocks.indexOf(correctStock)] = stock;
      }
    });
    this.syncVisibleBedsWithAllBeds();
  }

  onSortToggleChange(checked: boolean) {
    this.useCustomSort = checked;
    this.sortBedsOnLocationWithSlider();
  }
}
